import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { IAdministrators, IRegistrationState } from './store/state';
import { RegistrationAsync } from './store/async';
import { Router } from '@angular/router';
import { AppConfig } from '../shared/app.config';
import { isValidatingInfo } from './store/selector';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RegisterSubmitPayload } from './register/register.component';
import { CredentialsSubmitPayload } from './credentials/credentials.component';

interface ValidationTokenResponse {
  validationToken: string;
}

interface RegistrationPlansAllowed {
  [plan: string]: number;
}

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    standalone: false
})
export class RegistrationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  checkForPlan: RegistrationPlansAllowed = {};
  planFromUrl = '';

  constructor(private readonly router: Router,
              private readonly lux: Lux<{ registration: IRegistrationState }>,
              private readonly registrationAsync: RegistrationAsync,
              private readonly env: AppConfig) {
  }

  readonly credentials = signal(false);
  readonly confirmation = signal(false);
  readonly validationToken = signal('');
  readonly errorMessage = signal('');
  readonly userInfo = signal<IAdministrators | null>(null);
  readonly isValidatingInfo = toSignal(this.lux.get(isValidatingInfo), { initialValue: false });

  ngOnInit(): void {
    this.checkForPlan = this.env.plansAllowed as RegistrationPlansAllowed;
    this.planFromUrl = document.location.pathname.split('/')[1];
  }

  registerSubmit(event: RegisterSubmitPayload): void {
    const request = { ...event, adminPlan: this.checkForPlan[this.planFromUrl] };
    this.errorMessage.set('');

    this.registrationAsync.getValidationToken(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: ValidationTokenResponse | string) => {
        if (typeof response !== 'string' && response?.validationToken) {
          this.credentials.set(true);
          this.validationToken.set(response.validationToken);
          return;
        }

        this.errorMessage.set(typeof response === 'string' ? response : 'Unable to validate registration information.');
      });
  }

  credentialsSubmit(event: CredentialsSubmitPayload): void {
    this.errorMessage.set('');
    const request = {
      userIdentifier: event.userIdentifier,
      password: event.password,
      confirmPassword: event.confirmPassword,
      securityQuestion1: event.securityQuestion1 ?? '',
      securityAnswer1: event.securityAnswer1 ?? '',
      securityQuestion2: event.securityQuestion2 ?? '',
      securityAnswer2: event.securityAnswer2 ?? '',
      securityQuestion3: event.securityQuestion3 ?? '',
      securityAnswer3: event.securityAnswer3 ?? '',
      validationToken: this.validationToken(),
      adminPlan: this.checkForPlan[this.planFromUrl]
    };
    this.userInfo.set(request);

    this.registrationAsync.getAdministrators(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: { status: number } | string) => {
        if (typeof response !== 'string' && response.status === 201) {
          this.confirmation.set(true);
          return;
        }

        this.errorMessage.set(typeof response === 'string' ? response : 'Unable to complete registration.');
        window.scrollTo(0, 0);
      });
  }

}
